import { useEffect, useState } from 'react';
import { ACTION_TYPES, useBudgetProgramSearchDispatch, useBudgetProgramSearchState } from './budget-program-search-context';
import BudgetProgramAppService from '../../../../application/platform/sge/budget-program/budget-program-app-service';
import BudgetUnitAppService from '../../../../application/platform/sge/budget-unit/budget-unit-app-service';
import { useFeedback } from '../../../../hooks/use-feedback';
import { IBudgetProgramAddEditRequest } from '../../../../model/sge/budget-program/request/budget-program-add-edit-request';
import { IBudgetProgramResponse } from '../../../../model/sge/budget-program/response/budget-program-response';
import { parseNumberInput } from '../../../../utils/format';
import { formatCodeName } from '../../components/code-name-label';

export const useBudgetProgramSearchController = () => {
    const dispatch = useBudgetProgramSearchDispatch();
    const state = useBudgetProgramSearchState();
    const { showSuccess, showError, confirmDelete, withLoading } = useFeedback();
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        BudgetUnitAppService.list()
            .then((budgetUnits) => dispatch({ type: ACTION_TYPES.setBudgetUnits, payload: budgetUnits }))
            .catch((error: any) => showError('Não foi possível carregar as unidades orçamentárias', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filtros
    function handleOnChange(e: any) {
        const { name, value } = e.target;
        if (name === 'select-budget-unit') {
            dispatch({ type: ACTION_TYPES.updateParameter, payload: { budgetUnitId: parseNumberInput(value) } });
        } else if (name === 'input-search') {
            dispatch({ type: ACTION_TYPES.updateParameter, payload: { search: value || undefined } });
        }
    }

    function handleSearch() {
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    function handleClearFilters() {
        dispatch({ type: ACTION_TYPES.updateParameter, payload: { budgetUnitId: undefined, search: undefined } });
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    // Cadastro/edição: busca o registro atualizado antes de abrir o formulário
    async function openDrawer(row?: IBudgetProgramResponse) {
        if (!row) {
            dispatch({ type: ACTION_TYPES.openDrawer });
            return;
        }
        try {
            const budgetProgram = await withLoading(() => BudgetProgramAppService.getById(row.id));
            dispatch({ type: ACTION_TYPES.openDrawer, payload: budgetProgram });
        } catch (error: any) {
            showError('Não foi possível carregar o programa orçamentário', error);
        }
    }

    function closeDrawer() {
        dispatch({ type: ACTION_TYPES.closeDrawer });
    }

    async function handleSave(request: IBudgetProgramAddEditRequest) {
        const editing = state.drawer.record;
        setIsSaving(true);
        try {
            if (editing) {
                await BudgetProgramAppService.edit(editing.id, request);
                showSuccess('Programa orçamentário atualizado com sucesso!');
            } else {
                await BudgetProgramAppService.add(request);
                showSuccess('Programa orçamentário adicionado com sucesso!');
            }
            closeDrawer();
            dispatch({ type: ACTION_TYPES.triggerSearch });
        } catch (error: any) {
            showError('Não foi possível salvar o programa orçamentário', error);
        } finally {
            setIsSaving(false);
        }
    }

    async function removeBudgetProgram(row: IBudgetProgramResponse) {
        try {
            await BudgetProgramAppService.remove(row.id);
            showSuccess('Programa orçamentário excluído com sucesso!');
            dispatch({ type: ACTION_TYPES.triggerSearch });
        } catch (error: any) {
            showError('Não foi possível excluir o programa orçamentário', error);
        }
    }

    function handleDelete(row: IBudgetProgramResponse) {
        confirmDelete(`Tem certeza que deseja excluir o programa orçamentário "${formatCodeName(row)}"?`, () => { removeBudgetProgram(row); });
    }

    return { isSaving, handleOnChange, handleSearch, handleClearFilters, openDrawer, closeDrawer, handleSave, handleDelete };
};
